import React from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import NewBadge from 'src/components/NewBadge/NewBadge';
import {
  ApplyResult,
  UseEmailSelfServiceOrchestratorReturn,
  useEmailSelfServiceOrchestrator,
} from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useEmailSelfServiceOrchestrator';
import {
  StepId,
  WIZARD_STEP_PICK,
  WIZARD_STEP_REVIEW,
  WIZARD_STEP_CONFIRMATION,
} from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useEmailSelfServiceFormState/useEmailSelfServiceFormState';
import StepPickContacts from 'src/components/ContactsTable/EmailSelfServiceDialogue/StepPickContacts/StepPickContacts';
import StepReviewChanges from 'src/components/ContactsTable/EmailSelfServiceDialogue/StepReviewChanges/StepReviewChanges';
import StepConfirmation from 'src/components/ContactsTable/EmailSelfServiceDialogue/StepConfirmation/StepConfirmation';
import EmailSelfServiceFooter from 'src/components/ContactsTable/EmailSelfServiceDialogue/EmailSelfServiceFooter/EmailSelfServiceFooter';
import './email-self-service-dialogue.scss';

const STEP_DESCRIPTIONS: Record<StepId, string> = {
  1: 'Step 1 of 3 — Pick contacts to grant or revoke the ability to change their own email',
  2: 'Step 2 of 3 — Review changes before applying',
  3: 'Step 3 of 3 — All done',
};

export interface EmailSelfServiceDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (result: ApplyResult) => void;
}

const EmailSelfServiceDialogue = ({
  isOpen,
  onClose,
  onSuccess,
}: EmailSelfServiceDialogueProps) => {
  const orchestrator: UseEmailSelfServiceOrchestratorReturn =
    useEmailSelfServiceOrchestrator({ isOpen, onSuccess });

  const handleClose = (): void => {
    orchestrator.reset();
    onClose();
  };

  const handleApplyClick = (): void => {
    void orchestrator.handleApply();
  };

  const handleBackToPick = (): void => {
    orchestrator.goToStep(WIZARD_STEP_PICK);
  };

  const currentStep: StepId = orchestrator.currentStep;
  const stepDescription: string = STEP_DESCRIPTIONS[currentStep];

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <>
          Manage Email Self-Service
          <NewBadge spaced />
        </>
      }
      description={stepDescription}
      maxWidth="105rem"
      minHeight="95vh"
      footer={
        <EmailSelfServiceFooter
          currentStep={currentStep}
          canContinue={orchestrator.canContinueFromStep1}
          canApply={orchestrator.canApplyFromStep2}
          isSubmitting={orchestrator.isSubmitting}
          onCancel={handleClose}
          onBack={handleBackToPick}
          onContinue={orchestrator.stageSelectedAndContinue}
          onApply={handleApplyClick}
          onDone={handleClose}
        />
      }
    >
      <div className="essd-body">
        <div className="essd-body-content">
          {currentStep === WIZARD_STEP_PICK && (
            <StepPickContacts
              roleFilter={orchestrator.roleFilter}
              projectFilter={orchestrator.projectFilter}
              searchQuery={orchestrator.searchQuery}
              availableRoles={orchestrator.availableRoles}
              availableProjects={orchestrator.availableProjects}
              filteredContacts={orchestrator.filteredContacts}
              selectedIds={orchestrator.selectedIds}
              isLoadingContacts={orchestrator.isLoadingContacts}
              contactsError={orchestrator.contactsError}
              onRoleFilterChange={orchestrator.setRoleFilter}
              onProjectFilterChange={orchestrator.setProjectFilter}
              onSearchQueryChange={orchestrator.setSearchQuery}
              onToggleSelection={orchestrator.toggleSelection}
              onSetSelection={orchestrator.setSelection}
            />
          )}
          {currentStep === WIZARD_STEP_REVIEW && (
            <StepReviewChanges
              stagedEntries={orchestrator.stagedEntries}
              unchangedContacts={orchestrator.unchangedContacts}
              willAllowCount={orchestrator.willAllowIds.length}
              willDisallowCount={orchestrator.willDisallowIds.length}
              isSubmitting={orchestrator.isSubmitting}
              error={orchestrator.error}
              onBulkSetStaged={orchestrator.bulkSetStaged}
            />
          )}
          {currentStep === WIZARD_STEP_CONFIRMATION && (
            <StepConfirmation
              result={orchestrator.lastApplyResult}
              nowAllowedEntries={orchestrator.nowAllowedEntries}
              nowDisallowedEntries={orchestrator.nowDisallowedEntries}
              unchangedEntries={orchestrator.unchangedContacts}
            />
          )}
        </div>
      </div>
    </GenericDialogue>
  );
};

export default EmailSelfServiceDialogue;
